import { Badge, Card, Sheet, SheetContent, SheetHeader, SheetTitle } from "@devhop/ui";
import { Building2, Mail, User, Users } from "lucide-react";
import { AppImage } from "@/shared/ui/AppImage";
import type { PositionWithDetails } from "../../domain/types";

interface OrgChartSidebarProps {
  selectedPosition: PositionWithDetails | null;
  onClose: () => void;
}

export function OrgChartSidebar({
  selectedPosition,
  onClose,
}: OrgChartSidebarProps) {
  return (
    <Sheet
      open={!!selectedPosition}
      onOpenChange={(open) => !open && onClose()}
      data-org-chart-sidebar
    >
      <SheetContent
        side="right"
        className="w-80 p-4 sm:w-96"
        data-org-chart-sidebar
      >
        <SheetHeader>
          <SheetTitle>ລາຍລະອຽດຕໍາແໜ່ງ</SheetTitle>
        </SheetHeader>

        <div className="mt-6">
          {selectedPosition && (
            <div className="space-y-4">
              <Card className="p-4">
                <h3 className="mb-2 font-semibold text-lg">
                  {selectedPosition.title}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedPosition.department?.name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline">
                      Level {selectedPosition.level}
                    </Badge>
                  </div>
                </div>
              </Card>

              {selectedPosition.employees &&
              selectedPosition.employees.length > 0 ? (
                <Card className="p-4">
                  <h4 className="mb-3 flex items-center space-x-2 font-medium">
                    <Users className="h-4 w-4" />
                    <span>ພະນັກງານ ({selectedPosition.employees.length})</span>
                  </h4>
                  <div className="space-y-3">
                    {selectedPosition.employees.map((employee) => (
                      <div
                        key={employee.id}
                        className="flex items-start space-x-3 rounded-lg border p-2"
                      >
                        <div className="size-10 shrink-0 overflow-hidden rounded-full bg-muted">
                          {employee.image ? (
                            <AppImage
                              src={employee.image}
                              alt={employee.name}
                              className="size-full"
                              fit="cover"
                              showLoading={false}
                              fallback={
                                <div className="flex h-full w-full items-center justify-center bg-muted-foreground/20">
                                  <span className="font-medium text-muted-foreground text-xs">
                                    {employee.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              }
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-muted-foreground/20">
                              <span className="font-medium text-muted-foreground text-xs">
                                {employee.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="font-medium text-sm">{employee.name}</p>
                          <div className="flex items-center space-x-2 text-muted-foreground text-xs">
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{employee.email}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ) : (
                <Card className="border-dashed p-4">
                  <div className="text-center text-muted-foreground">
                    <User className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm">ຕໍາແໜ່ງນີ້ຍັງວ່າງ</p>
                  </div>
                </Card>
              )}

              {selectedPosition.reportsCount &&
                selectedPosition.reportsCount > 0 && (
                  <Card className="p-4">
                    <h4 className="mb-2 flex items-center space-x-2 font-medium">
                      <Users className="h-4 w-4" />
                      <span>ຕໍາແໜ່ງລ່ຽມ</span>
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      ມີຕໍາແໜ່ງລ່ຽມ {selectedPosition.reportsCount} ຕໍາແໜ່ງ
                    </p>
                  </Card>
                )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
